/* eslint-disable max-params */
import { SERVICE } from '@ds/swymkit/tools'
import { requirejsOne } from '@ds/swymkit/tools'
import { CSRF } from '@ds/swymkit/tools'
import { cacheDecorator } from '@ds/swymkit/tools'

import { swymFetch } from '~/services/fetch-swym'

import type { ECommunityRole } from '~/entities/container/community/interfaces/community'
import { userMembersListParser, userMembersSearchParser } from '~/entities/user/user/api/parser-search-user'

import { getSwymUrl } from '~/features-shared/config-manager/stores/swym-config'
import { logger } from '~/features-shared/libs/utils/logger'
import { proxifyUrl } from '~/features-shared/libs/utils/url-utils'
import type { IMember } from '~/features-shared/member/interfaces/search'

import type { ExaleadResponse, SwymError, SwymResponse } from '~/interfaces/services-dto'

interface getConversationListOpts {
  community_id: string
  cache?: boolean
  limit?: number
  page?: number
  filters?: { type: string, values: string | number }[]
}

export function getConversationList({
  community_id,
  cache = true,
  page = 1,
  limit = 20,
  filters,
}: getConversationListOpts) {
  return cacheDecorator(
    `members-list-${community_id}`,
    () => swymFetch.post('/api/community/listmembers', {
      json: {
        params: {
          as_admin: false,
          community_id,
          displayTotalCount: true,
          filters,
          limit,
          order_by_last_member: false,
          page,
        },
      },
    })
      .json<SwymResponse<IMember[]>>()
      .then(response => ({
        users: userMembersListParser(response),
        total: Number.parseInt(response?.nb_result || '0'),
      })),
    cache ? 60000 : 0,
  )
}

export function getCommunityOwnerList({
  community_id,
  cache = true,
  page = 1,
}: getConversationListOpts) {
  return cacheDecorator(
    `members-list-${community_id}`,
    () => swymFetch.post('/api/community/listmembers', {
      json: {
        params: {
          as_admin: false,
          community_id,
          displayTotalCount: true,
          filters: [
            {
              type: 'team_role_filter',
              values: 3,
            },
          ],
          limit: 9999,
          order_by_last_member: false,
          page,
          member_type: 'tenant_user',
        },
      },
    })
      .json<SwymResponse<IMember[]>>()
      .then(response => ({
        users: userMembersListParser(response),
        total: Number.parseInt(response?.nb_result || '0'),
      })),
    cache ? 60000 : 0,
  )
}

export function editMember(
  community_id: string,
  logins: string[],
  roles: ECommunityRole[],
) {
  return swymFetch.post('/api/community/editmembers', {
    json: {
      params: {
        as_admin: false,
        community_id,
        settings_members: logins.map((login, index) => `;add,${login},${roles[index]}`)
          .join(''),
      },
    },
  })
    .json()
}

export function changeRoleRequest(community_id: string, message: string): Promise<any> {
  return swymFetch.post('/api/notification/changerolerequest', {
    json: {
      params: {
        community_id,
        message,
      },
    },
  })
    .json()
}
export function addMember(
  community_id: string,
  logins: string[],
  roles: ECommunityRole[],
) {
  return swymFetch.post('/api/community/editmembers', {
    json: {
      params: {
        as_admin: false,
        community_id,
        settings_members: logins.map((login, index) => `;add,${login},${roles[index]}`)
          .join(''),
      },
    },
  })
    .json()
}

export function deleteMember(community_id: string, login: string) {
  return swymFetch.post('/api/community/editmembers', {
    json: {
      params: {
        as_admin: false,
        community_id,
        settings_members: `;delete,${login}`,
      },
    },
  })
    .json()
}

export function exportMembers(
  as_admin: boolean,
  community_id: string,
  format: string,
  filters?: string,
) {
  const filterURL = `/api/community/getteamfile/as_admin/${as_admin}/community_id/${community_id}/filters/${filters}/format/${format}`
  const URL = `/api/community/getteamfile/as_admin/${as_admin}/community_id/${community_id}/format/${format}`

  try {
    if (filters)
      return proxifyUrl(filterURL)
    else
      return proxifyUrl(URL)
  }
  catch (error) {
    logger.error(URL, error)
    return filters ? filterURL : URL
  }
}

export async function addbatch(
  processName: string,
  community_id: string,
  operationType: string,
  file_path: File,
  roleId?: string,
) {
  const fd = new FormData()
  fd.append('processName', processName)
  fd.append('communityId', community_id)
  fd.append('operationType', operationType)
  fd.append('userType', '4')
  fd.append('as_admin', 'false')
  if (roleId)
    fd.append('roleId', roleId)
  fd.append('file-path', file_path)

  const WAFData = await requirejsOne<any>('DS/WAFData/WAFData')
  const response = await new Promise((resolve, reject) => {
    WAFData.authenticatedRequest(`${getSwymUrl()}/api/member/addbatch`, {
      responseType: 'json',
      headers: { 'x-ds-swym-csrftoken': CSRF.get(SERVICE.SWYM) },
      type: 'json',
      method: 'POST',
      data: fd,
      onComplete: resolve,
      onFailure(error: Error['message'], response: Response) {
        const throwedError = new Error(error) as SwymError
        throwedError.response = response

        reject(throwedError.response)
      },
    })
  })

  return response
}
export function getBatchList(community_id: string) {
  return swymFetch.post('/api/member/getbatchlist', {
    json: {
      params: { start: 0, limit: 999, community_id, user_type: 4, as_admin: false },
    },
  })
    .json()
}

export function deleteProcess(id: string) {
  return swymFetch.post('/api/member/deleteprocess', {
    json: {
      params: { process_id: id, process_type: 1, as_admin: false },
    },
  })
    .json()
}

export function getProcessDetails(id: string) {
  return swymFetch.post('/api/member/getprocessresults', {
    json: {
      params: { id, start: 0, limit: 10, as_admin: false },
    },
  })
    .json()
}

export function exportProcessDetails(id: string) {
  return `/api/member/exportusers/id/${id}/as_admin/false`
}

interface getMembersOpts {
  community_id: string
  page?: number
  limit?: number
  query?: string
  refine?: string[]
}

export function getMembers({
  community_id,
  page = 1,
  query = '#all',
  refine,
  limit = 20,
}: getMembersOpts) {
  return swymFetch.post('/api/exalead/searchcommunitymembers', {
    json: {
      params: {
        ...(query && { query }),
        ...(refine && { refine }),
        community_id,
        nresults: limit,
        filters: null,
        displayTotalCount: true,
        as_admin: false,
        order_by_last_member: false,
        with_synthesis: true,
        contentType: 'user',
        legacyFormat: false,
        idForCommunityMembers: community_id,
        start: limit * (page - 1),
      },
    },
  })
    .json<ExaleadResponse>()
    .then(async response => ({
      users: await userMembersSearchParser(response as ExaleadResponse),
      groups: response?.groups,
      hits: response?.hits,
      total: Number.parseInt(response?.nhits.toString() || '0'),
    }))
}
